import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

export const BrandMark = ({ size = "md", withLabel = true }) => {
  const dim = size === "lg" ? 64 : 52;
  return (
    <Link
      to="/"
      className="flex items-center gap-3 group"
      data-testid="brand-link"
    >
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: dim, height: dim }}
        aria-hidden
      >
        <img
          src="/logo.png"
          alt="Lancers Studio"
          width={dim}
          height={dim}
          style={{
            width: dim,
            height: dim,
            objectFit: "contain",
            display: "block",
          }}
          onError={(e) => {
            // Fallback to gold-bordered "LS" monogram if logo.png is missing.
            const node = e.currentTarget;
            const parent = node.parentNode;
            if (!parent || parent.querySelector("[data-fallback-mark]")) return;
            node.style.display = "none";
            const fallback = document.createElement("span");
            fallback.setAttribute("data-fallback-mark", "true");
            fallback.className = "gold-border font-cinzel";
            fallback.style.cssText = [
              "position:absolute",
              "inset:0",
              "display:flex",
              "align-items:center",
              "justify-content:center",
              "border-radius:14px",
              "color:#f3d789",
              "font-weight:700",
              `font-size:${Math.round(dim * 0.42)}px`,
              "line-height:1",
              "letter-spacing:0.04em",
            ].join(";");
            fallback.textContent = "LS";
            parent.appendChild(fallback);
          }}
        />
      </span>
      {withLabel && (
        <span className="ls-brand gold-text" data-testid="brand-name">
          Lancers Studio
        </span>
      )}
    </Link>
  );
};

const Header = () => {
  const location = useLocation();

  // Highlight Services nav for both /services and consulting page is "Lancers Consulting"
  const isConsultingActive = location.pathname === "/consulting";
  const isServicesActive = location.pathname === "/services";

  return (
    <header
      className="ls-header sticky top-0 z-50 w-full"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
        <BrandMark />

        <nav
          className="hidden md:flex items-center gap-8"
          data-testid="primary-nav"
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `ls-nav-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </NavLink>
          ))}
          {(isConsultingActive || isServicesActive) && (
            <span
              className="ls-nav-link active"
              data-testid="nav-active-section"
            >
              {isConsultingActive ? "Lancers Consulting" : "Lancers Services"}
            </span>
          )}
        </nav>

        <div className="md:hidden">
          <NavLink
            to="/services"
            className="ls-nav-link"
            data-testid="nav-mobile-services"
          >
            Services
          </NavLink>
        </div>
      </div>
    </header>
  );
};

const Footer = ({ note }) => {
  return (
    <footer
      className="w-full mt-20 pt-10 pb-10 border-t border-white/5"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4 opacity-80">
          <BrandMark size="md" withLabel={true} />
        </div>
        <p
          className="text-[0.82rem] tracking-wide"
          style={{ color: "var(--ls-muted-2)" }}
          data-testid="footer-note"
        >
          {note ||
            "© 2026 Lancers Studio. Expert resources, technical capability, and ready-to-deploy teams."}
        </p>
      </div>
    </footer>
  );
};

const Layout = ({ children, footerNote }) => {
  return (
    <div className="App relative">
      <Header />
      <main className="relative">{children}</main>
      <Footer note={footerNote} />
    </div>
  );
};

export default Layout;
